import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiPlus, FiTag, FiX, FiTrash2, FiEdit2, FiCheck, FiSave } = FiIcons;

function CategoryManager({ categories, onClose, onCreate, onUpdate, onDelete, onDeleteMany }) {
  const [form, setForm] = useState({ name: '', description: '', image_url: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', description: '', image_url: '', active: true });
  const [editSaving, setEditSaving] = useState(false);

  const update = (key) => (event) => {
    setForm((current) => ({ ...current, [key]: event.target.value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      setError('Enter a category name.');
      return;
    }

    setSaving(true);
    setError('');
    const result = await onCreate(form);
    setSaving(false);

    if (result?.error) {
      setError(result.error);
      return;
    }

    setForm({ name: '', description: '', image_url: '' });
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === categories.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(categories.map((c) => c.id)));
    }
  };

  const handleDeleteOne = async (category) => {
    if (!confirm(`Delete category "${category.name}"?`)) return;
    await onDelete(category.id);
    setSelected((prev) => {
      const next = new Set(prev);
      next.delete(category.id);
      return next;
    });
  };

  const handleDeleteSelected = async () => {
    if (selected.size === 0) return;
    if (!confirm(`Delete ${selected.size} selected categor${selected.size === 1 ? 'y' : 'ies'}?`)) return;
    setDeleting(true);
    await onDeleteMany([...selected]);
    setSelected(new Set());
    setDeleting(false);
  };

  const startEdit = (category) => {
    setEditingId(category.id);
    setEditForm({
      name: category.name || '',
      description: category.description || '',
      image_url: category.image_url || '',
      active: category.active !== false
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ name: '', description: '', image_url: '', active: true });
  };

  const saveEdit = async () => {
    if (!editForm.name.trim()) return;
    setEditSaving(true);
    await onUpdate(editingId, editForm);
    setEditSaving(false);
    cancelEdit();
  };

  const updateEdit = (key) => (event) => {
    if (key === 'active') {
      setEditForm((current) => ({ ...current, active: event.target.checked }));
    } else {
      setEditForm((current) => ({ ...current, [key]: event.target.value }));
    }
  };

  const allSelected = categories.length > 0 && selected.size === categories.length;

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal admin-category-modal" onClick={(event) => event.stopPropagation()}>
        <div className="admin-modal-header">
          <div>
            <h2>Product Categories</h2>
            <p>Create and edit reusable categories for products and the landing page.</p>
          </div>
          <button className="admin-icon-btn" onClick={onClose} aria-label="Close">
            <SafeIcon icon={FiX} />
          </button>
        </div>

        <div className="admin-modal-body category-manager-body">
          <form className="category-create-card" onSubmit={submit}>
            <div className="category-create-heading">
              <SafeIcon icon={FiPlus} />
              <h3>Create Category</h3>
            </div>
            {error && <div className="category-error">{error}</div>}
            <div className="editor-field">
              <label>Category Name</label>
              <input value={form.name} onChange={update('name')} placeholder="e.g. Herbal Powders" />
            </div>
            <div className="editor-field">
              <label>Landing Page Description</label>
              <textarea value={form.description} onChange={update('description')} rows={3} />
            </div>
            <div className="editor-field">
              <label>Landing Page Image URL</label>
              <input value={form.image_url} onChange={update('image_url')} placeholder="https://..." />
            </div>
            <button className="admin-btn primary" type="submit" disabled={saving}>
              <SafeIcon icon={FiPlus} /> {saving ? 'Saving…' : 'Save Category'}
            </button>
          </form>

          <div className="saved-category-list">
            <div className="saved-category-header-row">
              <h3>Saved Categories</h3>
              {categories.length > 0 && (
                <label className="batch-select-label">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                  />
                  Select All
                </label>
              )}
            </div>

            {selected.size > 0 && (
              <div className="batch-action-bar">
                <span>{selected.size} selected</span>
                <button
                  className="admin-btn secondary batch-delete-btn"
                  onClick={handleDeleteSelected}
                  disabled={deleting}
                >
                  <SafeIcon icon={FiTrash2} /> {deleting ? 'Deleting…' : 'Delete Selected'}
                </button>
              </div>
            )}

            {categories.map((category) => (
              <div className="saved-category-row" key={category.id || category.name}>
                {editingId === category.id ? (
                  <div className="category-edit-form">
                    <div className="category-edit-fields">
                      <div className="editor-field">
                        <label>Name</label>
                        <input
                          value={editForm.name}
                          onChange={updateEdit('name')}
                          autoFocus
                        />
                      </div>
                      <div className="editor-field">
                        <label>Description</label>
                        <textarea
                          value={editForm.description}
                          onChange={updateEdit('description')}
                          rows={2}
                        />
                      </div>
                      <div className="editor-field">
                        <label>Image URL</label>
                        <input
                          value={editForm.image_url}
                          onChange={updateEdit('image_url')}
                        />
                      </div>
                      {editForm.image_url && (
                        <img
                          src={editForm.image_url}
                          alt="Preview"
                          className="category-edit-preview"
                        />
                      )}
                      <label className="editor-checkbox">
                        <input
                          type="checkbox"
                          checked={editForm.active}
                          onChange={updateEdit('active')}
                        />
                        Active (visible on storefront)
                      </label>
                    </div>
                    <div className="category-edit-actions">
                      <button
                        className="admin-btn primary"
                        onClick={saveEdit}
                        disabled={editSaving || !editForm.name.trim()}
                      >
                        <SafeIcon icon={FiSave} /> {editSaving ? 'Saving…' : 'Save'}
                      </button>
                      <button className="admin-btn secondary" onClick={cancelEdit}>
                        <SafeIcon icon={FiX} /> Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <input
                      type="checkbox"
                      className="category-select-checkbox"
                      checked={selected.has(category.id)}
                      onChange={() => toggleSelect(category.id)}
                    />
                    <span className="saved-category-icon"><SafeIcon icon={FiTag} /></span>
                    <div className="saved-category-info">
                      <strong>
                        {category.name}
                        {!category.active && (
                          <span className="category-inactive-badge">Hidden</span>
                        )}
                      </strong>
                      <small>{category.description || 'No landing-page description'}</small>
                    </div>
                    <div className="admin-row-actions">
                      <button
                        className="admin-icon-btn"
                        onClick={() => startEdit(category)}
                        title="Edit category"
                      >
                        <SafeIcon icon={FiEdit2} />
                      </button>
                      <button
                        className="admin-icon-btn danger"
                        onClick={() => handleDeleteOne(category)}
                        title="Delete category"
                      >
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CategoryManager;